import React, { useEffect, useState } from "react";
import { collection, onSnapshot, updateDoc, doc } from "firebase/firestore";
import { db } from "./firebaseConfig";
import { Card, CardContent } from "./components/ui/card";

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [prevOrdersCount, setPrevOrdersCount] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(false);
  const [totalSales, setTotalSales] = useState(0);
  const exchangeRate = 89500; // Exchange rate for USD to LBP

  useEffect(() => {
    const ordersRef = collection(db, "orders");

    const unsubscribe = onSnapshot(ordersRef, (snapshot) => {
      const updatedOrders = snapshot.docs
        .map((doc) => ({
          id: doc.id,
          ...doc.data(),
          items: doc.data().items || [],
          status: doc.data().status || "pending",
          discountedTotal: doc.data().discountedTotal || null,
        }))
        .filter((order) => order.status !== "processed");

      if (soundEnabled && updatedOrders.length > prevOrdersCount) {
        playNotificationSound();
      }

      setPrevOrdersCount(updatedOrders.length);
      setOrders(updatedOrders);
      console.log("📡 Orders updated in real-time:", updatedOrders);
    });

    return () => unsubscribe();
  }, [prevOrdersCount, soundEnabled]);

  useEffect(() => {
    const enableSound = () => {
      setSoundEnabled(true);
      document.removeEventListener("click", enableSound);
      console.log("🔊 Sound enabled after user interaction.");
    };

    document.addEventListener("click", enableSound);
    return () => document.removeEventListener("click", enableSound);
  }, []);

  const playNotificationSound = () => {
    const sound = new Audio("/notification.mp3");
    sound.volume = 1.0;
    sound.play().catch((err) => console.warn("🔇 Sound blocked:", err));
  };

  useEffect(() => {
    const resetSales = () => {
      setTotalSales(0);
      console.log("🕒 Total sales reset after 16 hours");
    };

    const interval = setInterval(resetSales, 16 * 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const processOrder = async (order, applyDiscount = false) => {
    console.log("🛠 Processing order:", order);
    try {
      const discountedTotal = applyDiscount ? (order.total * 0.875).toFixed(2) : order.total;
      const orderRef = doc(db, "orders", order.id);

      await updateDoc(orderRef, { status: "processed", discountedTotal });

      setTotalSales((prevTotal) => prevTotal + parseFloat(discountedTotal));

      alert(`✅ Order processed successfully! Final Price: $${discountedTotal} 
            IN LBP: ${(discountedTotal * exchangeRate) % 5000 > 2500 
            ? ((discountedTotal * exchangeRate) - (discountedTotal * exchangeRate) % 5000 + 5000) 
            : ((discountedTotal * exchangeRate) - (discountedTotal * exchangeRate) % 5000)}`);
    } catch (error) {
      console.error("❌ Error processing order:", error);
      alert("⚠️ Failed to process order.");
    }
  };

  return (
    <div className="bg-black text-gold p-6 min-h-screen">
      <h1 className="text-3xl font-bold mb-4 text-center">Admin Orders</h1>

      {/* ✅ Total Sales Section */}
      <div className="bg-gray-800 text-white p-4 rounded-lg mb-6 text-center">
        <h2 className="text-2xl font-bold">Total Sales (Last 16 Hours)</h2>
        <p className="text-xl">💰 ${totalSales.toFixed(2)}</p>
        <p className="text-xl">💰 LBP {(totalSales * exchangeRate) % 5000 > 2500 
          ? ((totalSales * exchangeRate) - (totalSales * exchangeRate) % 5000 + 5000) 
          : ((totalSales * exchangeRate) - (totalSales * exchangeRate) % 5000)} LBP</p>
      </div>

      {orders.length > 0 ? (
        <div className="grid grid-cols-1 gap-4">
          {orders.map((order) => (
            <Card key={order.id} className="bg-gray-900 p-4 rounded-lg shadow-md border border-gold">
              <CardContent>
                <h2 className="text-xl font-bold text-gold">Order ID: {order.id}</h2>
                <p className="text-md text-gray-400">Date: {order.date ? new Date(order.date).toLocaleString() : "Unknown"}</p>
                <p className="text-lg text-gray-400 font-semibold">Total: {order.total}$</p>
                <p className="text-lg text-gray-400 font-semibold">
                  Total in LBP: {(order.total * exchangeRate) % 5000 > 2500 
                    ? ((order.total * exchangeRate) - (order.total * exchangeRate) % 5000 + 5000) 
                    : ((order.total * exchangeRate) - (order.total * exchangeRate) % 5000)} LBP
                </p>
                <p className="text-lg text-gray-400">Type: {order.orderType}</p>
                
                <div className="mt-2">
                  <h3 className="text-md font-semibold text-gold">Items Ordered:</h3>
                  <ul className="text-gray-400">
                    {order.items.length > 0 ? (
                      order.items.map((item, index) => (
                        <li key={index}>- {item.name} (x{item.quantity})</li>
                      ))
                    ) : (
                      <li className="text-gray-400">No items found</li>
                    )}
                  </ul>
                </div>

                {order.orderType === "delivery" && (
                  <div className="mt-2">
                    <h3 className="text-md font-semibold text-gold">Customer Details:</h3>
                    <p>Name: {order.customerInfo?.name || "N/A"}</p>
                    <p>Phone: {order.customerInfo?.phone || "N/A"}</p>
                    <p>Address: {order.customerInfo?.address || "N/A"}</p>
                  </div>
                )}

                <div className="flex gap-2 mt-4">
                  <button onClick={() => processOrder(order, false)} className="bg-gold text-black px-4 py-2 rounded-md hover:bg-yellow-600 transition">
                    Process Order
                  </button>
                  <button onClick={() => processOrder(order, true)} className="bg-green-500 text-black px-4 py-2 rounded-md hover:bg-green-700 transition">
                    Process with Discount
                  </button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <p className="text-center text-xl">No pending orders.</p>
      )}
    </div>
  );
};

export default AdminOrders;

